import { useEffect, useState, type ReactNode } from 'react'
import { fetchTimezoneSuggestion, useUpdateFacility } from '../index.js'
import type { Facility, TimezoneSuggestion, UpdateFacilityInput } from '../index.js'
import { TimeZoneSelect } from './TimeZoneSelect.js'
import { addressFromDraft, canSuggestTimezone, draftFromAddress, formatAddress, hasAddressContent, missingAddressFields, normalisedDraft, type AddressDraft } from './facilityAddress.js'
import { Button, HelpIcon } from '../../core/index.js'
import { Save } from 'lucide-react'
import { CountryInput, PhoneInput, RegionInput, regionLabelOf } from '../../core/index.js'

const INPUT_CLASS = 'w-full rounded border border-marine/30 bg-white px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20'
const SUGGESTION_DELAY_MS = 400

function Field({ label, htmlFor, children, span }: { label: string; htmlFor: string; children: ReactNode; span?: boolean }) {
  return (
    <div className={span ? 'sm:col-span-2' : ''}>
      <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  )
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-start py-2.5 border-b border-marine/10 last:border-b-0 gap-4">
      <div className="text-xs text-ink-mid">{label}</div>
      <div className="text-sm font-bold text-ink text-right">{value}</div>
    </div>
  )
}

/**
 * Asks the API where a postal code falls once the person has typed five
 * digits, a moment after they stop typing. Null until there is something to
 * ask about.
 */
function useTimezoneSuggestion(draft: AddressDraft, enabled: boolean): TimezoneSuggestion | null {
  const [suggestion, setSuggestion] = useState<TimezoneSuggestion | null>(null)
  const postalCode = draft.postalCode.trim()
  const country = draft.country.trim().toUpperCase()
  const shouldAsk = enabled && canSuggestTimezone(draft)

  useEffect(() => {
    if (!shouldAsk) {
      setSuggestion(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      fetchTimezoneSuggestion(postalCode, country)
        .then((result) => {
          if (!cancelled) {
            setSuggestion(result)
          }
        })
        .catch(() => {
          // A failed lookup just means no suggestion; the select still works.
          if (!cancelled) {
            setSuggestion(null)
          }
        })
    }, SUGGESTION_DELAY_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [shouldAsk, postalCode, country])

  return suggestion
}

function SuggestionNote({ suggestion, current, onUse }: { suggestion: TimezoneSuggestion; current: string; onUse: (zone: string) => void }) {
  if (suggestion.timezone === null) {
    return (
      <p className="text-[11px] text-ink-mid mt-1">
        {suggestion.reason === 'unsupported-country' ? 'Time zone suggestions cover US postal codes only.' : `No time zone is known for postal code ${suggestion.postalCode}.`}
      </p>
    )
  }
  if (suggestion.timezone === current) {
    return <p className="text-[11px] text-ink-mid mt-1">Matches the time zone for postal code {suggestion.postalCode}.</p>
  }
  return (
    <p className="text-[11px] text-ink-mid mt-1">
      Postal code {suggestion.postalCode} is in <span className="font-bold text-ink">{suggestion.timezone.replaceAll('_', ' ')}</span>.{' '}
      <button type="button" onClick={() => onUse(suggestion.timezone ?? '')} className="font-bold text-marine hover:text-marine-dk">
        Use it
      </button>
    </p>
  )
}

/** The API's own rule for a phone number: digits, spaces and the usual punctuation, at least one digit. */
const PHONE_PATTERN = /^(?=.*[0-9])[0-9+()\-.\s]+$/

interface FacilityDetailsCardProps {
  facility: Facility
  /** True for an operator session. A facility administrator sees the details but cannot change them. */
  canEdit: boolean
  onUnsavedChange?: (hasUnsaved: boolean) => void
}

/**
 * A facility's existence record - name, time zone and address - as the
 * operator keeps it. The address is the operator's alone to set (the
 * platform admin never can), and typing a US postal code suggests the zone,
 * which fills the select only while it is unset and otherwise waits to be
 * accepted: a few ZIP codes straddle a zone line.
 */
export function FacilityDetailsCard({ facility, canEdit, onUnsavedChange }: FacilityDetailsCardProps) {
  const updateMutation = useUpdateFacility(facility.facilityId)
  const [name, setName] = useState(facility.name)
  const [timezone, setTimezone] = useState(facility.timezone ?? '')
  const [address, setAddress] = useState<AddressDraft>(() => draftFromAddress(facility.address))
  const [phone, setPhone] = useState(facility.phone ?? '')
  const [validationError, setValidationError] = useState<string | null>(null)
  // Once the person has chosen a zone themselves, a suggestion only ever offers; it never overwrites.
  const [hasChosenTimezone, setHasChosenTimezone] = useState(false)
  const suggestion = useTimezoneSuggestion(address, canEdit)

  // The suggestion fills a zone that was never set, and only until the person touches it.
  useEffect(() => {
    if (!hasChosenTimezone && timezone === '' && suggestion?.timezone) {
      setTimezone(suggestion.timezone)
    }
  }, [suggestion, timezone, hasChosenTimezone])

  // After a save the list refetches and the facility prop catches up; until it does, the draft
  // is compared against what was just saved so the card reads as clean straight away.
  const saved = updateMutation.data ?? facility
  const savedAddress = draftFromAddress(saved.address)
  const addressDirty = JSON.stringify(normalisedDraft(address)) !== JSON.stringify(savedAddress)
  const phoneDirty = phone.trim() !== (saved.phone ?? '')
  const isDirty = name.trim() !== saved.name || timezone !== (saved.timezone ?? '') || addressDirty || phoneDirty
  const timezoneChanged = saved.timezone !== undefined && timezone !== saved.timezone

  function chooseTimezone(zone: string): void {
    setHasChosenTimezone(true)
    setTimezone(zone)
  }

  useEffect(() => {
    onUnsavedChange?.(isDirty)
    return () => onUnsavedChange?.(false)
  }, [isDirty, onUnsavedChange])

  if (!canEdit) {
    return (
      <div className="bg-white border border-marine/15 rounded p-5">
        <div className="text-sm font-black text-ink mb-2">Facility Details<HelpIcon topic="facilities.config-details" className="ml-1" /></div>
        <ReadOnlyRow label="Name" value={facility.name} />
        <ReadOnlyRow label="Time zone" value={facility.timezone?.replaceAll('_', ' ') ?? 'Not set (reads as UTC)'} />
        <ReadOnlyRow label="Address" value={facility.address ? formatAddress(facility.address) : 'Not set'} />
        <ReadOnlyRow label="Phone" value={facility.phone ?? 'Not set'} />
        <p className="text-[11px] text-ink-mid leading-relaxed pt-2">Set by your operator. Ask them to change any of these.</p>
      </div>
    )
  }

  function updateAddress(patch: Partial<AddressDraft>): void {
    setAddress((prev) => ({ ...prev, ...patch }))
  }

  function buildInput(): UpdateFacilityInput | string {
    const input: UpdateFacilityInput = {}
    if (name.trim() === '') {
      return 'The facility needs a name.'
    }
    if (name.trim() !== saved.name) {
      input.name = name.trim()
    }
    if (timezone !== (saved.timezone ?? '') && timezone !== '') {
      input.timezone = timezone
    }
    if (addressDirty) {
      if (!hasAddressContent(address)) {
        input.address = null
      } else {
        const missing = missingAddressFields(address)
        if (missing.length > 0) {
          return `The address still needs: ${missing.join(', ')}.`
        }
        input.address = addressFromDraft(address)
      }
    }
    if (phoneDirty) {
      const trimmed = phone.trim()
      if (trimmed !== '' && !PHONE_PATTERN.test(trimmed)) {
        return 'The phone number may hold only digits, spaces, +, (, ), - and . with at least one digit.'
      }
      input.phone = trimmed === '' ? null : trimmed
    }
    return input
  }

  function handleSave(): void {
    const input = buildInput()
    if (typeof input === 'string') {
      setValidationError(input)
      return
    }
    setValidationError(null)
    updateMutation.mutate(input)
  }

  const idPrefix = `facility-${facility.facilityId}`
  const error = validationError ?? (updateMutation.isError ? updateMutation.error.message : null)

  return (
    <div className="bg-white border border-marine/15 rounded p-5">
      <div className="text-sm font-black text-ink mb-3">Facility Details<HelpIcon topic="facilities.config-details" className="ml-1" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" htmlFor={`${idPrefix}-name`}>
          <input id={`${idPrefix}-name`} className={INPUT_CLASS} value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Time zone" htmlFor={`${idPrefix}-timezone`}>
          <TimeZoneSelect id={`${idPrefix}-timezone`} label="Time zone" className={INPUT_CLASS} value={timezone} onChange={chooseTimezone} />
          {suggestion && <SuggestionNote suggestion={suggestion} current={timezone} onUse={chooseTimezone} />}
          {timezoneChanged && (
            <p className="text-[11px] text-coral mt-1">
              Changing the time zone moves this facility's day and period boundaries. Running scores can change at the next recalculation.
            </p>
          )}
        </Field>
        <Field label="Street" htmlFor={`${idPrefix}-line1`} span>
          <input id={`${idPrefix}-line1`} className={INPUT_CLASS} value={address.line1} autoComplete="address-line1" onChange={(event) => updateAddress({ line1: event.target.value })} />
        </Field>
        <Field label="Street, line 2 (optional)" htmlFor={`${idPrefix}-line2`} span>
          <input id={`${idPrefix}-line2`} className={INPUT_CLASS} value={address.line2} autoComplete="address-line2" onChange={(event) => updateAddress({ line2: event.target.value })} />
        </Field>
        <Field label="City" htmlFor={`${idPrefix}-city`}>
          <input id={`${idPrefix}-city`} className={INPUT_CLASS} value={address.city} autoComplete="address-level2" onChange={(event) => updateAddress({ city: event.target.value })} />
        </Field>
        <Field label={regionLabelOf(address.country)} htmlFor={`${idPrefix}-region`}>
          <RegionInput id={`${idPrefix}-region`} className={INPUT_CLASS} country={address.country} value={address.region} onChange={(region) => updateAddress({ region })} />
        </Field>
        <Field label="Postal code" htmlFor={`${idPrefix}-postal`}>
          <input id={`${idPrefix}-postal`} className={INPUT_CLASS} value={address.postalCode} autoComplete="postal-code" onChange={(event) => updateAddress({ postalCode: event.target.value })} />
        </Field>
        <Field label="Country" htmlFor={`${idPrefix}-country`}>
          <CountryInput id={`${idPrefix}-country`} className={INPUT_CLASS} value={address.country} onChange={(country) => updateAddress({ country })} />
        </Field>
        <Field label="Phone (optional)" htmlFor={`${idPrefix}-phone`}>
          <PhoneInput id={`${idPrefix}-phone`} className={INPUT_CLASS} value={phone} maxLength={32} onChange={setPhone} />
        </Field>
      </div>
      <p className="text-[11px] text-ink-mid leading-relaxed pt-3">
        The time zone is the one this facility's days and scoring periods are reckoned in. Typing a US postal code suggests it. The facility's own staff -
        its operator and administrators - set the address and phone; SeeWhoCares cannot change them for you. Clear every address field, or the phone, to remove it.
      </p>
      <div className="flex items-center gap-3 mt-3">
        <Button icon={Save} disabled={!isDirty || updateMutation.isPending} onClick={handleSave}>
          {updateMutation.isPending ? 'Saving…' : 'Save Details'}
        </Button>
        {!isDirty && updateMutation.isSuccess && <span className="text-xs font-bold text-marine">Saved.</span>}
        {error && <span className="text-xs font-bold text-coral">{error}</span>}
      </div>
    </div>
  )
}
